import { useEffect } from "react";
import { useI18n, SUPPORTED_LOCALES, type Locale } from "@/lib/i18n";
import { localePath } from "@/lib/localePath";
import { webPageJsonLd } from "@/lib/seoSchemas";

type SeoProps = {
  title?: string;
  description?: string;
  /** Canonical TR-form path (e.g. "/felsefemiz"). Locale prefix is applied automatically. */
  path?: string;
  image?: string;
  type?: "website" | "article";
  jsonLd?: Record<string, any> | Record<string, any>[];
  /**
   * Yalnızca Türkçe yayınlanan sayfalar (ilçe/bölge landing sayfaları) için.
   * Canonical her zaman TR adrese sabitlenir ve hreflang alternatifi BASILMAZ —
   * aksi halde 7 dilin tamamı aynı Türkçe metni gösteren kopya URL olarak görünür.
   */
  trOnly?: boolean;
  /**
   * Bu SAYFANIN gerçekten çevrildiği diller (örn. blog yazısı — sadece tr+en).
   * Verilmezse tüm SUPPORTED_LOCALES varsayılır (statik/hizmet sayfaları gibi
   * gerçekten 7 dilde var olan sayfalar için doğru varsayım). 16 Eylül 2026
   * denetiminde bulundu: bu prop olmadan her sayfa koşulsuz 7 dilde hreflang
   * basıyordu — çevirisi olmayan blog yazılarında hreflang, prerender'ın
   * ATLADIĞI ve nginx'in SPA fallback ile TR ana sayfayı yanlış canonical'la
   * sunduğu URL'leri "gerçek dil sürümü" diye işaretliyordu.
   */
  availableLocales?: Locale[];
  /**
   * Slug'ı dile göre değişen sayfalar (localize edilmiş blog slug'ları) için —
   * verilen dildeki hreflang href'i `path`+prefix yerine bu değeri kullanır.
   * Yoksa o dil `path` üzerinden prefix eklenerek üretilir (statik/hizmet
   * sayfaları gibi tüm dillerde AYNI slug'ı paylaşanlar için doğru varsayım).
   */
  alternatePaths?: Partial<Record<Locale, string>>;
  /** Blog yazısı gibi tarihli içerikler için — WebPage düğümüne eklenir. */
  datePublished?: string;
  dateModified?: string;
};

const OG_LOCALE: Record<Locale, string> = {
  tr: "tr_TR", en: "en_US", de: "de_DE", es: "es_ES", ru: "ru_RU", fr: "fr_FR", it: "it_IT",
};

const SITE_URL = "https://anadoluterra.com";
const DEFAULT_IMAGE = "https://anadoluterra.com/og-image.jpg";

function setMeta(attr: "name" | "property", key: string, content: string) {
  if (!content) return;
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    if (hreflang) el.hreflang = hreflang;
    document.head.appendChild(el);
  }
  el.href = href;
}

function removeAllAlternates() {
  document.head
    .querySelectorAll('link[rel="alternate"][hreflang]')
    .forEach((n) => n.remove());
}

export function Seo({ title, description, path = "/", image, type = "website", jsonLd, trOnly = false, availableLocales, alternatePaths, datePublished, dateModified }: SeoProps) {
  const { locale } = useI18n();
  useEffect(() => {
    const fullTitle = title
      ? `${title} | Anadolu Terra`
      : "Anadolu Terra | Güvenlik · Temizlik · Zırhlı Araç · Tesis Yönetimi";
    const desc =
      description ||
      "2004'ten bugüne Türkiye'nin önde gelen Güvenlik, Zırhlı Araç Tahsisi ve Taşıma, İlaçlama, Temizlik ve Tesis Yönetimi çözüm ortağı.";
    const canonicalUrl = trOnly ? `${SITE_URL}${path}` : `${SITE_URL}${localePath(path, locale)}`;
    const img = image || DEFAULT_IMAGE;

    document.title = fullTitle;
    setMeta("name", "description", desc);
    setLink("canonical", canonicalUrl);

    // hreflang alternates — point each locale to its real translated URL.
    // availableLocales verilmişse (örn. blog yazısı) sadece o diller basılır —
    // aksi halde çevirisi olmayan bir dile "burada gerçek sürüm var" denmiş olur.
    removeAllAlternates();
    if (!trOnly) {
      const locales = availableLocales ?? SUPPORTED_LOCALES;
      locales.forEach((l) => {
        const altPath = alternatePaths?.[l] ?? path;
        setLink("alternate", `${SITE_URL}${localePath(altPath, l)}`, l);
      });
      // x-default → TR (root) version — tr her zaman gerçek olduğu için sabit.
      const xDefaultPath = alternatePaths?.tr ?? path;
      setLink("alternate", `${SITE_URL}${localePath(xDefaultPath, "tr")}`, "x-default");
    }

    // Open Graph
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", canonicalUrl);
    setMeta("property", "og:type", type);
    setMeta("property", "og:image", img);
    setMeta("property", "og:site_name", "Anadolu Terra");
    setMeta("property", "og:locale", OG_LOCALE[locale] || "tr_TR");

    // Twitter
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", img);

    // GEO
    setMeta("name", "geo.region", "TR-34");
    setMeta("name", "geo.placename", "Ataşehir, İstanbul");
    setMeta("name", "geo.position", "40.984639;29.136611");
    setMeta("name", "ICBM", "40.984639, 29.136611");

    // JSON-LD (page-scoped). Supports array → multiple <script> tags.
    // Her sayfa otomatik bir WebPage düğümü alır (@id, isPartOf→WebSite,
    // about→Organization, varsa datePublished/dateModified) — 16 Eylül 2026
    // denetiminde bulundu: bu düğüm hiç yoktu, sayfalar arası @id bağı eksikti.
    document.querySelectorAll('script[data-page-jsonld="true"]').forEach((n) => n.remove());
    {
      const webPage = webPageJsonLd({
        url: canonicalUrl,
        name: fullTitle,
        description: desc,
        inLanguage: locale,
        datePublished,
        dateModified,
      });
      const list = jsonLd ? [webPage, ...(Array.isArray(jsonLd) ? jsonLd : [jsonLd])] : [webPage];
      list.forEach((entry, i) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.dataset.pageJsonld = "true";
        script.id = `page-jsonld-${i}`;
        script.text = JSON.stringify(entry);
        document.head.appendChild(script);
      });
    }
    return () => {
      document.querySelectorAll('script[data-page-jsonld="true"]').forEach((n) => n.remove());
    };
  }, [title, description, path, image, type, locale, trOnly, JSON.stringify(jsonLd), JSON.stringify(availableLocales), JSON.stringify(alternatePaths), datePublished, dateModified]);

  return null;
}
